import { useQuery } from '@tanstack/react-query';

import { api, type Capability, type Role } from '@/lib/api';
// `useOptionalSession`, not `useSession`: this hook fails open when it cannot
// find out who is signed in, so it must not be the thing that throws.
import { useOptionalSession } from '@/app/session';
import { useT } from '@/lib/i18n';

/**
 * `useCan` — what the signed-in operator may do (`plan-preproduction` R3).
 *
 * ### This is an affordance. It is not, and must never become, enforcement.
 *
 * Every gated route refuses on its own — `requireCapability` in the middleware,
 * and the tests that assert a 403 for an analyst are unchanged. CLAUDE.md is
 * explicit that permission is *"enforced server-side on every request, never by
 * hiding a control"*, and nothing here weakens that. A user who edits their
 * stored session to say `admin` gains exactly nothing: the role the server reads
 * comes from a signed token, never from the client.
 *
 * What this fixes is a different problem, which the console had badly.
 * [QcReview.tsx] argued that hiding a control is not what stops an analyst
 * pressing it — correct — and concluded that the console should therefore render
 * every control identically for everyone and let the server say no. That is
 * defensible exactly once. Across roughly thirty controls it is a product where
 * a read-only operator's every click is a red toast, and where nothing on screen
 * ever tells them whose job the thing they wanted actually is.
 *
 * ### So: disable and explain. Do not hide.
 *
 * `cannotBecause(capability)` returns the sentence a `title` or tooltip should
 * carry — "Requires the administrator role" — built from the roles the server
 * says hold that capability. Naming the ROLE rather than the capability is
 * deliberate: `catalogue:publish` is our vocabulary, and an operator who needs
 * to go and ask someone needs to know who to ask.
 *
 * Hiding is right in exactly one place: a nav item to a screen with nothing
 * readable on it for that role. There are none of those today.
 *
 * ### Why the matrix is fetched rather than written here
 *
 * Because it was written here once already — six literals in `Settings.tsx`,
 * rendering a Psychometrician column for three phases while the role did not
 * exist. `GET /api/auth/capabilities` serves `ROLE_CAPABILITIES` itself, so the
 * table an operator reads and the rule the server enforces are the same object.
 *
 * `staleTime: Infinity` because policy cannot change inside a session: it is
 * compiled into the server. A role *change* arrives with a new token.
 */
export function useCapabilityMatrix() {
  return useQuery({
    queryKey: ['capabilities'],
    queryFn: api.capabilities,
    staleTime: Infinity,
    // A retry loop on this would disable every control on every screen for as
    // long as it kept failing. One attempt, then fall back — see `useCan`.
    retry: false,
  });
}

export interface Permissions {
  /** Does the signed-in operator hold `capability`? */
  can: (capability: Capability) => boolean;
  /** Which roles hold it — for the tooltip, and for the Settings table. */
  rolesWith: (capability: Capability) => Role[];
  /** True until the matrix has loaded; controls should not flicker on it. */
  loading: boolean;
}

export function usePermissions(): Permissions {
  const user = useOptionalSession()?.user ?? null;
  const { data, isLoading } = useCapabilityMatrix();

  /**
   * **Fails OPEN, deliberately, and it is safe because this is not the gate.**
   *
   * If the matrix has not loaded — a slow network, a request that failed — the
   * choice is between disabling every control until it arrives, or letting the
   * click through to the server that was always the thing deciding. Disabling
   * would make a flaky network look like a demotion, and would do it silently.
   * Letting it through costs an analyst one 403 in the case where the console
   * could not find out, which is exactly the behaviour before this hook existed.
   *
   * **Optional all the way down, for the same reason.** `data.roles[user.role]`
   * read `undefined.includes` and threw the moment the response was not the
   * shape expected — which crashed *every screen using a permission*, turning
   * "we could not find out who you are" into a white page. A hook whose whole
   * contract is to degrade quietly must not be the thing that throws; the
   * smoke tests caught this by returning a generic stub payload.
   */
  const held = Array.isArray(data?.roles?.[user?.role as Role])
    ? data!.roles[user!.role]
    : null;

  return {
    can: (capability) => (held ? held.includes(capability) : true),
    rolesWith: (capability) =>
      data?.roles
        ? (Object.keys(data.roles) as Role[]).filter((role) =>
            (data.roles[role] ?? []).includes(capability),
          )
        : [],
    loading: isLoading,
  };
}

/** The single-capability form, which is what a control usually wants. */
export function useCan(capability: Capability): boolean {
  return usePermissions().can(capability);
}

const ROLE_STRING = {
  admin: 'role.admin',
  psychometrician: 'role.psychometrician',
  analyst: 'role.analyst',
} as const;

/**
 * The sentence a disabled control carries — `null` when the caller may act.
 *
 * Returns a *reason*, not a boolean, so a component cannot disable a control
 * without also saying why. The two have to travel together or the fix is only
 * half made: a greyed button with no explanation is worse than a 403, because
 * the 403 at least had a message.
 */
export function useCannotBecause(capability: Capability): string | null {
  const t = useT();
  const { can, rolesWith } = usePermissions();
  if (can(capability)) return null;

  const roles = rolesWith(capability).map((role) => t(ROLE_STRING[role]));
  return roles.length > 0 ? t('permission.requiresRole', { roles: roles.join(' / ') }) : null;
}
